import type { User } from "~/models/user/User";
import isNil from "../validation/isNil";
import Role from "~/models/user/Role";
import { z } from "zod";
import type { EnumType } from "../enums/EnumType";
import { isMemberOfCompany } from "./isMemberOfCompany";

export const RepresentationType = Object.freeze({
  Admin: "admin",
  Anonymous: "anonymous",
  Company: "company",
  Customer: "customer",
});

export type RepresentationType = EnumType<typeof RepresentationType>;

export const representationSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal(RepresentationType.Admin) }),
  z.object({ type: z.literal(RepresentationType.Anonymous) }),
  z.object({ type: z.literal(RepresentationType.Customer) }),
  z.object({
    type: z.literal(RepresentationType.Company),
    companyId: z.string(),
  }),
]);

export function determineRepresentation(
  user: User | null,
  rawContext: unknown,
) {
  const ctx = representationSchema.safeParse(rawContext).data;

  if (isNil(user)) {
    return {
      type: RepresentationType.Anonymous,
    };
  }

  if (isNil(ctx)) {
    switch (user.role) {
      case Role.Company:
        if (user.companyMemberships.length === 0) {
          return { type: RepresentationType.Anonymous };
        }

        return {
          type: RepresentationType.Company,
          companyId: user.companyMemberships[0].companyId,
        };
      case Role.Customer:
        return { type: RepresentationType.Customer };
      case Role.Admin:
        return { type: RepresentationType.Admin };
      case Role.CompanyAndCustomer:
        return { type: RepresentationType.Customer };
    }
  }

  switch (user.role) {
    case Role.Company:
      if (user.companyMemberships.length === 0) {
        return { type: RepresentationType.Anonymous };
      }

      if (
        ctx.type === RepresentationType.Company &&
        isMemberOfCompany(user, ctx.companyId)
      ) {
        return ctx;
      }

      return {
        type: RepresentationType.Company,
        companyId: user.companyMemberships[0].companyId,
      };
    case Role.Customer:
      return { type: RepresentationType.Customer };
    case Role.Admin:
      return { type: RepresentationType.Admin };
    case Role.CompanyAndCustomer:
      if (ctx.type !== RepresentationType.Company) {
        return {
          type: RepresentationType.Customer,
        };
      }

      if (isMemberOfCompany(user, ctx.companyId)) {
        return ctx;
      }

      if (user.companyMemberships.length === 0) {
        return { type: RepresentationType.Customer };
      }

      return {
        type: RepresentationType.Company,
        companyId: user.companyMemberships[0].companyId,
      };
  }
}
